/* eslint-disable */
document.addEventListener('DOMContentLoaded', () => {
  /**
   * Waits until the SyneriseTC object is available and ready.
   *
   * @returns {Promise<object>} Resolves with the SyneriseTC object.
   */
  function waitForSynerise() {
    return new Promise((resolve, reject) => {
      const maxAttempts = 50; // 5 seconds max wait (50 * 100ms)
      let attempts = 0;

      /**
       * Check if SyneriseTC is ready
       */
      const check = () => {
        // eslint-disable-next-line no-undef
        if (typeof SyneriseTC !== 'undefined' && SyneriseTC.isReady) {
          // eslint-disable-next-line no-undef
          resolve(SyneriseTC);
        } else if (attempts >= maxAttempts) {
          // eslint-disable-next-line no-console
          console.error('SyneriseTC timeout: not ready after 5 seconds');
          reject(new Error('SyneriseTC timeout'));
        } else {
          // eslint-disable-next-line no-plusplus
          attempts++;
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  /**
   * Parse a price value to number
   * @param {string|number} price - The price value
   * @returns {number} - Parsed price as number
   */
  function parsePrice(price) {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      return parseFloat(price.replace(/\s*PLN\s*$/i, '').replace(/\s/g, '').replace(',', '.') || '0');
    }
    return 0;
  }

  /**
   * Remove only pad_color from Shopify CDN image URL, preserve width/height.
   * @param {string} url
   * @returns {string}
   */
  function cleanImageUrl(url) {
    if (!url || typeof url !== 'string') return url;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.delete('pad_color');
      const qs = u.searchParams.toString();
      return `${u.origin}${u.pathname}${qs ? `?${qs}` : ''}`;
    } catch (e) {
      // Fallback: strip pad_color from raw query string
      return url.replace(/([?&])pad_color=[^&]*(&|$)/, (m, p1, p2) => (p2 === '&' ? p1 : ''))
        .replace(/\?[&]+/, '?')
        .replace(/\?$/, '');
    }
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} - Escaped string
   */
  function escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Normalize text for comparison (case-insensitive, remove diacritics)
   * @param {string} text - Text to normalize
   * @returns {string} - Normalized text
   */
  function normalizeText(text) {
    if (!text) return '';
    const normalized = text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/\s+/g, ' ') // collapse spaces
      .trim();
    return normalized;
  }

  const TRUSTMATE_DEFAULT_STAR_SRC = 'https://trustmate.io/images/widgets/rating/stars/star-34.svg';

  function getTrustmateStarImg(src) {
    const safeSrc = escapeHtml(src || TRUSTMATE_DEFAULT_STAR_SRC);
    return `<img src="${safeSrc}" width="40" height="38" loading="lazy" decoding="async" alt="" />`;
  }

  function renderStar(fillAmount = 0, starSrc = TRUSTMATE_DEFAULT_STAR_SRC) {
    const starImg = getTrustmateStarImg(starSrc);
    if (fillAmount >= 1) {
      return `<span class="synerise-trustmate__star synerise-trustmate__star--full">${starImg}</span>`;
    }
    if (fillAmount <= 0) {
      return `<span class="synerise-trustmate__star synerise-trustmate__star--empty">${starImg}</span>`;
    }
    const percentage = Math.round(fillAmount * 100);
    return `
      <span class="synerise-trustmate__star synerise-trustmate__star--partial" style="--trustmate-star-fill:${percentage}%;">
        <span class="synerise-trustmate__star-layer synerise-trustmate__star-layer--dimmed">
          ${starImg}
        </span>
        <span class="synerise-trustmate__star-layer synerise-trustmate__star-layer--filled">
          ${starImg}
        </span>
      </span>
    `;
  }

  /**
   * Get badge type and display text based on label and settings
   * @param {string} label - Badge label to check
   * @param {object} badgeSettings - Badge settings from wrapper
   * @returns {object} - Object with type and text properties
   */
  function getBadgeInfo(label, badgeSettings) {
    if (!label) return { type: '', text: label };

    const normalizedLabel = normalizeText(label);
    const {
      newTags,
      hotTags,
      comingTags,
      selseyTags,
      odooNewLabel,
      odooBestsellerLabel,
    } = badgeSettings;

    // Tag configuration: maps tag lists to badge types and Odoo overrides
    const tagConfigs = [
      {
        tags: newTags,
        type: 'new',
        odooType: 'odo-new',
        odooLabel: odooNewLabel,
      },
      {
        tags: hotTags,
        type: 'hot',
        odooType: 'odo-bestseller',
        odooLabel: odooBestsellerLabel,
      },
      {
        tags: comingTags,
        type: 'coming',
        odooType: null,
        odooLabel: null,
      },
      {
        tags: selseyTags,
        type: 'selsey',
        odooType: null,
        odooLabel: null,
      },
    ];

    // Check against tag lists
    for (const config of tagConfigs) {
      const normalizedTags = config.tags.map((tag) => normalizeText(tag));
      if (normalizedTags.includes(normalizedLabel)) {
        // If Odoo override exists, use it
        if (config.odooType && config.odooLabel) {
          return { type: config.odooType, text: config.odooLabel };
        }
        return { type: config.type, text: label };
      }
    }

    // Fallback mappings for common badge names
    const fallbackMappings = [
      {
        match: ['nowosc', 'wielkanoc'],
        type: 'odo-new',
        fallbackType: 'new',
        label: odooNewLabel,
      },
      {
        match: ['bestseller'],
        type: 'odo-bestseller',
        fallbackType: null,
        label: odooBestsellerLabel,
      },
      {
        match: ['hot'],
        type: 'hot',
        fallbackType: null,
        label: null,
      },
      {
        match: ['coming', 'coming soon'],
        type: 'coming',
        fallbackType: null,
        label: null,
      },
    ];

    for (const mapping of fallbackMappings) {
      if (mapping.match.includes(normalizedLabel)) {
        // Use Odoo type if label exists, otherwise use fallback type or default
        if (mapping.label) {
          return { type: mapping.type, text: mapping.label };
        }
        if (mapping.fallbackType) {
          return { type: mapping.fallbackType, text: label };
        }
        return { type: mapping.type, text: label };
      }
    }

    // Default fallback - badge doesn't match any tags
    return { type: '', text: label };
  }

  /**
   * Check if a value is valid (not null, false, empty string, or 'null')
   * @param {*} value - Value to check
   * @returns {boolean} - True if value is valid
   */
  function isValidValue(value) {
    return value && value !== 'null' && value !== null && value !== false && value !== '';
  }

  /**
   * Add badge to array if not already added
   * @param {Array} badges - Array of badges
   * @param {Set} addedTypes - Set of already added badge types
   * @param {object} badgeInfo - Badge info object with type and text
   * @param {string} sourceLabel - Source label for debugging
   */
  function addBadgeIfNotDuplicate(badges, addedTypes, badgeInfo, sourceLabel = '') {
    if (!badgeInfo.type) {
      return;
    }

    if (addedTypes.has(badgeInfo.type)) {
      return;
    }

    badges.push(badgeInfo);
    addedTypes.add(badgeInfo.type);
  }

  /**
   * Render badges for a product
   * @param {object} item - Product item from API
   * @param {object} badgeSettings - Badge settings from wrapper
   * @returns {string} - HTML string for badges
   */
  function renderBadges(item, badgeSettings) {
    const attributes = item.attributes || {};
    const badges = [];
    const addedBadgeTypes = new Set();

    // Parse badge string (comma-separated) and add badges
    if (attributes.badge) {
      const badgeList = attributes.badge
        .split(',')
        .map((b) => b.trim())
        .filter((b) => b.length > 0);

      badgeList.forEach((badgeLabel) => {
        const badgeInfo = getBadgeInfo(badgeLabel, badgeSettings);
        addBadgeIfNotDuplicate(badges, addedBadgeTypes, badgeInfo, badgeLabel);
      });
    }

    // Odoo badge configurations
    const odooBadges = [
      {
        attribute: 'bestseller',
        check: (val) => val === 'true' || val === true,
        type: 'odo-bestseller',
        text: badgeSettings.odooBestsellerLabel,
      },
      {
        attribute: 'new',
        check: isValidValue,
        type: 'odo-new',
        text: badgeSettings.odooNewLabel,
      },
      {
        attribute: 'express_delivery',
        check: isValidValue,
        type: 'odo-express-delivery',
        text: null, // Use value from API
      },
    ];

    // Add Odoo badges if conditions are met
    odooBadges.forEach((config) => {
      const value = attributes[config.attribute];
      if (config.check(value)) {
        const badgeInfo = {
          type: config.type,
          text: config.text || value,
        };
        addBadgeIfNotDuplicate(badges, addedBadgeTypes, badgeInfo);
      }
    });

    // Generate HTML for badges
    if (badges.length === 0) {
      return '';
    }

    const badgesHtml = badges
      .map((badge) => {
        const badgeType = badge.type ? `f-badge--${badge.type}` : '';
        const escapedText = escapeHtml(badge.text);
        return `<span class="f-badge ${badgeType}" aria-hidden="true">${escapedText}</span>`;
      })
      .join('');

    return `<div class="product-card__badge">${badgesHtml}</div>`;
  }

  /**
   * Render Trustmate rating widget
   * @param {object} item - Product item from API
   * @returns {string} - HTML string for rating widget
   */
  function renderTrustmateRating(item, starSrc = TRUSTMATE_DEFAULT_STAR_SRC) {
    const averageGradeRaw = item.attributes?.average_grade;
    const reviewsCountRaw = item.attributes?.reviews_count;
    const normalizedAverage = averageGradeRaw
      ? parseFloat(String(averageGradeRaw).replace(',', '.'))
      : 0;
    const safeAverage = Number.isFinite(normalizedAverage) ? normalizedAverage : 0;
    const reviewsCount = reviewsCountRaw ? parseInt(reviewsCountRaw, 10) : 0;
    const safeReviews = Number.isFinite(reviewsCount) ? reviewsCount : 0;
    const hasReviews = safeAverage > 0 && safeReviews > 0;
    const ratingValue = hasReviews ? Math.min(Math.max(safeAverage, 0), 5) : 0;

    const starsHtml = Array.from({ length: 5 }, (_, index) => {
      if (!hasReviews) {
        return renderStar(0, starSrc);
      }
      const fillValue = Math.max(Math.min(ratingValue - index, 1), 0);
      return renderStar(fillValue, starSrc);
    }).join('');

    const ratingText = hasReviews
      ? `${escapeHtml(ratingValue.toFixed(1))}<span>(${escapeHtml(String(safeReviews))})</span>`
      : `<span>(${escapeHtml(String(safeReviews))})</span>`;

    return `
      <div class="synerise-trustmate${hasReviews ? '' : ' synerise-trustmate--no-reviews'}">
        <div class="synerise-trustmate__stars">
          ${starsHtml}
        </div>
        <div class="synerise-trustmate__text">${ratingText}</div>
      </div>
    `;
  }

  /**
   * Renders products inside a Swiper slider.
   *
   * @param {HTMLElement} sliderEl - The slider container element.
   * @param {Array<object>} products - Array of product objects.
   * @param {HTMLElement} wrapperEl - The wrapper element with translation data attributes.
   */
  function renderProducts(sliderEl, products, wrapperEl) {
    // eslint-disable-next-line no-param-reassign
    sliderEl.innerHTML = '';

    // Get translation data from wrapper
    const soldOutText = wrapperEl?.dataset.soldOutText || '';
    const installmentLabel = wrapperEl?.dataset.installmentLabel;
    const fromLabel = wrapperEl?.dataset.fromLabel;
    const installmentCount = parseInt(wrapperEl?.dataset.installmentCount || '10', 10);
    const omnibusText = wrapperEl?.dataset.omnibusText;
    const ratLabel = wrapperEl?.dataset.ratLabel;
    const currencySymbol = window.CURRENCY_SYMBOL || 'zł';
    const trustmateStarSrc = wrapperEl?.dataset.trustmateStarSrc || TRUSTMATE_DEFAULT_STAR_SRC;

    // Get badge settings from wrapper
    const badgeSettings = {
      newTags: wrapperEl?.dataset.newTags ? wrapperEl.dataset.newTags.split(',').map((t) => t.trim()).filter((t) => t) : [],
      hotTags: wrapperEl?.dataset.hotTags ? wrapperEl.dataset.hotTags.split(',').map((t) => t.trim()).filter((t) => t) : [],
      comingTags: wrapperEl?.dataset.comingTags ? wrapperEl.dataset.comingTags.split(',').map((t) => t.trim()).filter((t) => t) : [],
      selseyTags: wrapperEl?.dataset.selseyTags ? wrapperEl.dataset.selseyTags.split(',').map((t) => t.trim()).filter((t) => t) : [],
      odooNewLabel: wrapperEl?.dataset.odooNewLabel || '',
      odooBestsellerLabel: wrapperEl?.dataset.odooBestsellerLabel || '',
    };

    const wrapper = document.createElement('div');
    wrapper.classList.add('swiper-wrapper');

    products.forEach((item) => {
      const slide = document.createElement('div');
      slide.classList.add('swiper-slide');
      console.log(item);
      // Handle both old format (Google Shopping with g:* fields) and new format
      // Check availability: availability flag AND stock level (stan_magazynowy > 0)
      const stockLevel = parseFloat(item.attributes?.stan_magazynowy || '0');
      const isItemAvailable = item.availability !== false && stockLevel > 0;

      const normalizedItem = {
        image: item.imageLink,
        groupId: item.itemGroupId,
        variantId: item.attributes?.variant_id,
        price: item.price?.value || item.price,
        salePrice: item.salePrice?.value || item.salePrice,
        link: item.link,
        id: item.itemId,
        title: item.title,
        isAvailable: isItemAvailable,
        omnibusPrice: item.attributes?.omnibus_price,
        seria: item.attributes?.seria,
      };

      // Determine secondary image for hover (match product card behavior)
      let secondaryImage = '';
      if (Array.isArray(item.additionalImageLinks)) {
        if (item.additionalImageLinks.length === 1 && typeof item.additionalImageLinks[0] === 'string' && item.additionalImageLinks[0].includes(',')) {
          // Handle single comma-separated string inside array
          const splitted = item.additionalImageLinks[0]
            .split(',')
            .map((u) => u.trim())
            .filter(Boolean);
          secondaryImage = splitted.find((u) => u && u !== normalizedItem.image) || splitted[0] || '';
        } else {
          secondaryImage = item.additionalImageLinks.find((u) => u && u !== normalizedItem.image) || item.additionalImageLinks[0] || '';
        }
      } else if (typeof item.additionalImageLinks === 'string') {
        const splitted = item.additionalImageLinks
          .split(',')
          .map((u) => u.trim())
          .filter(Boolean);
        secondaryImage = splitted.find((u) => u && u !== normalizedItem.image) || splitted[0] || '';
      }

      // Parse prices as numbers
      const numericPrice = parsePrice(normalizedItem.price);
      const numericSalePrice = normalizedItem.salePrice ? parsePrice(normalizedItem.salePrice) : 0;

      // Treat the lower of price/salePrice as the current price and the higher as compare-at
      const candidates = [numericPrice, numericSalePrice].filter((v) => v > 0);
      let isOnSale = false;
      let displayPrice = 0;
      let displayComparePrice = null;

      if (candidates.length >= 2) {
        const minVal = Math.min(...candidates);
        const maxVal = Math.max(...candidates);
        isOnSale = minVal < maxVal;
        displayPrice = minVal;
        displayComparePrice = isOnSale ? maxVal : null;
      } else {
        displayPrice = candidates[0] || 0;
        displayComparePrice = null;
        isOnSale = false;
      }

      const formattedPrice = displayPrice.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      const formattedComparePrice = displayComparePrice ? displayComparePrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

      // Calculate installment price based on sale price if available (no decimals)
      const installmentPrice = displayPrice / installmentCount;
      const formattedInstallmentPrice = `${Math.round(installmentPrice)}`;

      const mainImageSrc = cleanImageUrl(normalizedItem?.image);
      const secondaryImageSrc = cleanImageUrl(secondaryImage);
      const formId = `quick-add-form-${normalizedItem?.groupId}`;
      const buttonId = `quick-add-button-${normalizedItem?.groupId}`;
      const escapedTitle = escapeHtml(normalizedItem?.title || '');
      const escapedSeria = normalizedItem?.seria ? escapeHtml(normalizedItem.seria) : '';
      const escapedSoldOutText = escapeHtml(soldOutText);
      const escapedOmnibusText = escapeHtml(omnibusText || '');
      const escapedFromLabel = escapeHtml(fromLabel || '');
      const escapedRatLabel = escapeHtml(ratLabel || '');
      const trustmateHtml = renderTrustmateRating(item, trustmateStarSrc);

      // Render badges
      const badgesHtml = renderBadges(item, badgeSettings);

      slide.innerHTML = `
        <a href="${escapeHtml(normalizedItem?.link || '#')}" class="synerise-item">
          <div class="synerise-item__img" style="padding: 0;">
            ${badgesHtml}
            <div class="product-card__image-wrapper ${secondaryImage ? '' : 'product-card__image-wrapper--main-only'}">
              <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1/1">
                <img src="${escapeHtml(mainImageSrc)}" alt="${escapedTitle}">
              </div>
              ${secondaryImage ? `
                <div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: 1/1">
                  <img class="absolute inset-0 w-full h-full" style="object-fit: cover; object-position: center;" src="${escapeHtml(secondaryImageSrc)}" alt="${escapedTitle}">
                </div>
              ` : ''}
            </div>
            <form method="post" action="/cart/add" id="${formId}" accept-charset="UTF-8" class="product-card__actions product-card__main-actions" enctype="multipart/form-data" novalidate data-type="add-to-cart-form" is="product-form">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${escapeHtml(normalizedItem?.variantId || normalizedItem?.groupId || '')}" class="product-variant-id">
              <button id="${buttonId}" type="submit" name="add" aria-label="Add to cart" class="product-card__atc product-card__action-button btn btn--white${!normalizedItem?.isAvailable ? ' product-card__action-button--full' : ''}" ${!normalizedItem?.isAvailable ? 'disabled' : ''}>
                <div class="btn__text flex gap-1 items-center">
                  ${normalizedItem?.isAvailable ? `
                    <span class="product-card__atc-icon product-card__action-icon inline-flex">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M5.90002 17.75H7.90002V19.75H5.90002V21.75H3.90002V19.75H1.90002V17.75H3.90002V15.75H5.90002V17.75ZM18.6774 7.81152L22.1002 7.8125V9.8125L20.9332 9.81152L20.1764 18.8955C20.133 19.4135 19.7001 19.8123 19.1803 19.8125H9.60022V17.8125H18.2594L18.9264 9.81152L5.27307 9.8125L5.64319 14.25H3.63635L3.26624 9.81152L2.10022 9.8125V7.8125L5.52209 7.81152L8.73401 2.25L10.4664 3.25L7.83264 7.81152H16.3668L13.734 3.25L15.4664 2.25L18.6774 7.81152ZM9.10022 15.8125H7.10022V11.8125H9.10022V15.8125ZM13.1002 15.8125H11.1002V11.8125H13.1002V15.8125ZM17.1002 15.8125H15.1002V11.8125H17.1002V15.8125Z" fill="white"/>
                      </svg>
                    </span>
                  ` : `
                    <span class="product-card__atc-text product-card__action-text">${escapedSoldOutText}</span>
                  `}
                </div>
                <div class="loading__spinner hidden">
                  <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>
            </form>
          </div>
          ${escapedSeria ? `<span class="product-card__seria">${escapedSeria}</span>` : ''}
          <h3 class="synerise-item__title">
            <span class="reversed-link block">
              <span class="reversed-link__text">
                ${escapedTitle}
              </span>
            </span>
          </h3>
          <div class="product-card__trustmate-target">
            ${trustmateHtml}
          </div>
          <div class="f-price ${isOnSale ? 'f-price--on-sale' : ''}">
            <div class="f-price__regular">
              <span class="f-price-item f-price-item--regular">${currencySymbol}${displayComparePrice ? formattedComparePrice : formattedPrice}</span>
            </div>
            <div class="f-price__sale">
              <span class="f-price-item f-price-item--sale">${currencySymbol}${formattedPrice}</span>
              ${displayComparePrice ? `
                <span class="f-price-item f-price-item--regular">
                  <s>${currencySymbol}${formattedComparePrice}</s>
                </span>
              ` : ''}
            </div>
          </div>
          ${normalizedItem?.omnibusPrice && normalizedItem.omnibusPrice !== '0' && normalizedItem.omnibusPrice !== 0 ? `
            <p class="synerise-item__omnibus">${escapedOmnibusText} ${Math.round(parseFloat(normalizedItem.omnibusPrice))} ${currencySymbol}</p>
          ` : ''}
          <span class="product-card__separator"></span>
        </a>
      `;

      wrapper.appendChild(slide);
    });

    sliderEl.appendChild(wrapper);

    let disablePagination = false;
    const isCartDrawer = sliderEl.classList.contains('synerise-recommendations__slider--cart-drawer');
    if (products.length <= 4 && window.innerWidth > 1026) {
      disablePagination = true;
    } else if (products.length <= 3 && window.innerWidth > 769 && window.innerWidth < 1025) {
      disablePagination = true;
    } else if (!isCartDrawer && !window.location.href.includes('products')) {
      disablePagination = true
    }

    // Add pagination and navigation if missing
    if (!sliderEl.querySelector('.swiper-pagination') && !disablePagination) {
      const pagination = document.createElement('div');
      pagination.classList.add('swiper-pagination');
      sliderEl.appendChild(pagination);
    }
    if (!sliderEl.querySelector('.swiper-button-next') && !disablePagination) {
      const next = document.createElement('div');
      next.classList.add('swiper-button-next');
      sliderEl.appendChild(next);
    }
    if (!sliderEl.querySelector('.swiper-button-prev') && !disablePagination) {
      const prev = document.createElement('div');
      prev.classList.add('swiper-button-prev');
      sliderEl.appendChild(prev);
    }
  }

  /**
   * Initializes Swiper instance for a given slider element.
   *
   * @param {HTMLElement} sliderEl - The slider container element.
   */
  function initSwiper(sliderEl) {
    if (!window.Swiper) {
      // eslint-disable-next-line no-console
      console.error('Swiper is not loaded!');
      return;
    }

    if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

    const isCartDrawer = sliderEl.classList.contains('synerise-recommendations__slider--cart-drawer');

    // eslint-disable-next-line no-new, no-undef
    new Swiper(sliderEl, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      loop: true,
      grabCursor: true,
      breakpoints: isCartDrawer
        ? {
            480: {
              slidesPerView: 2,
              spaceBetween: 15,
            },
            768: {
              slidesPerView: 2,
              spaceBetween: 15,
            },
            1024: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
          }
        : {
            766: {
              slidesPerView: 2.2,
              spaceBetween: 15,
            },
            900: {
              slidesPerView: 3.2,
              spaceBetween: 15,
            },
            1200: {
              slidesPerView: 4,
              spaceBetween: 20,
            },
          },
      navigation: {
        nextEl: sliderEl.querySelector('.swiper-button-next'),
        prevEl: sliderEl.querySelector('.swiper-button-prev'),
      },
    });
  }

  /**
   * Handle tabs
   *
   * @param {HTMLElement} container - The tabs container element
   */
  function initTabs(container) {
    const tabs = container.querySelectorAll('.synerise-tab');
    const contents = container.querySelectorAll('.tab-content');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-tab');
        tabs.forEach((t) => t.classList.remove('is-active'));
        contents.forEach((c) => c.classList.remove('is-active'));

        tab.classList.add('is-active');
        const activeContent = container.querySelector(`.tab-content[data-tab-content="${target}"]`);
        if (activeContent) activeContent.classList.add('is-active');
      });
    });
  }

  // Main async function
  const initSyneriseRecommendations = async () => {
    try {
      const tc = await waitForSynerise();
      const syneriseUrl = window.SYNERISE_API_BASE;
      const trackerKey = window.SYNERISE_TRACKER_KEY;

      const sections = document.querySelectorAll('.synerise-recommendations');

      sections.forEach(async (section) => {
        const sliderEl = section.querySelector('.synerise-recommendations__slider');
        if (!sliderEl) return;

        // Skip if already has content and it's not the cart drawer (which we WANT to refresh)
        const isCartDrawer = sliderEl.classList.contains('synerise-recommendations__slider--cart-drawer');
        const swiperWrapper = sliderEl.querySelector('.swiper-wrapper');
        if (swiperWrapper && swiperWrapper.children.length > 0 && !isCartDrawer) {
          return;
        }

        // Find wrapper with translation data attributes
        const wrapperEl = sliderEl.closest('.synerise-recommendations-wrapper') ||
                         document.querySelector('.synerise-recommendations-wrapper');

        const { recommendations: campaignId, product: productId } = sliderEl.dataset;

        if (!campaignId) {
          // eslint-disable-next-line no-param-reassign
          section.style.display = 'none';
          return;
        }

        try {
          const url = new URL(`${syneriseUrl}/recommendations/v2/recommend/campaigns/${campaignId}`);
          url.searchParams.set('token', trackerKey);
          url.searchParams.set('clientUUID', tc.uuid);

          if (productId) {
            url.searchParams.set('itemId', productId);
          }

          // Check additional attributes
          const productsAttr = sliderEl.getAttribute('data-products');
          const placementAttr = sliderEl.getAttribute('data-placement');

          if (productsAttr) {
            const items = productsAttr.split(',').map((p) => p.trim());
            url.searchParams.set('items', JSON.stringify(items));
          }

          if (placementAttr) {
            url.searchParams.set('placement', placementAttr);
          }

          const res = await fetch(url.toString());

          // Handle 204 No Content (no recommendations)
          if (res.status === 204) {
            // eslint-disable-next-line no-param-reassign
            section.style.display = 'none';
            return;
          }

          if (!res.ok) throw new Error(`API error: ${res.status}`);
          const data = await res.json();

          const products = data.data || [];
          if (!products.length) {
            // eslint-disable-next-line no-param-reassign
            section.style.display = 'none';
            return;
          }

          renderProducts(sliderEl, products, wrapperEl);

          // eslint-disable-next-line no-param-reassign
          section.style.display = '';
          section.classList.add('active');

          initSwiper(sliderEl);

          const wrap = section.closest('.synerise-recommendations-wrap') || section.querySelector('.synerise-recommendations-wrap') || document.querySelector('.synerise-recommendations-wrap');
          if (wrap) {
            wrap.style.display = '';
            wrap.classList.add('active');
          }
          const title = section.querySelector('.synerise-recommendations__title') || document.querySelector('.synerise-recommendations__title');
          if (title) {
            title.style.display = '';
          }
          if (wrap) {
            initTabs(wrap);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(`Fetch error for campaign ${campaignId}:`, err);
          // eslint-disable-next-line no-param-reassign
          section.style.display = 'none';
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong in waitForSynerise:', err);
      // Hide all sections if SyneriseTC failed to load
      document.querySelectorAll('.synerise-recommendations').forEach((s) => {
        // eslint-disable-next-line no-param-reassign
        s.style.display = 'none';
      });
    }
  }

  // Run on load
  initSyneriseRecommendations();

  // Re-run on cart update
  document.addEventListener('cart:updated', () => {
    initSyneriseRecommendations();
  });

  // Expose to global scope for manual calls
  window.initSyneriseRecommendations = initSyneriseRecommendations;
});
